import lodashIsEqual from 'lodash/isEqual';
import uniqWith from 'lodash/uniqWith';

import type { FiltersFormRule } from 'components/powerFlowShared/components/nodeTypes/canvas/filters';
import { MatchingOperator } from 'components/powerFlowShared/components/types';
import {
  FlowCategorySelectItem,
  FlowType,
  getSystemVariableString,
  SystemVariableId,
  SystemVariableType,
} from 'fable/flowEditor';

type Operator = {
  id: MatchingOperator;
  name: string;
};

type OperatorKey = keyof typeof operatorItems;

export type ScopeVariables = {
  available: string[];
};

const {
  EQUALS,
  NOT_EQUALS,
  IS_EMPTY,
  IS_NOT_EMPTY,
  CONTAINS,
  NOT_CONTAINS,
  STARTS_WITH,
  ENDS_WITH,
  GREATER_THAN,
  LESS_THAN,
  GREATER_THAN_OR_EQUAL,
  LESS_THAN_OR_EQUAL,
} = MatchingOperator;

const {
  Body,
  CallerNumber,
  CCEmails,
  Channel,
  CSID,
  Date,
  Direction,
  FromEmail,
  FromName,
  Language,
  ReceivingNumber,
  Subject,
  SubjectOrBody,
  ToEmail,
  ContactCustomAttributePrefix,
  CustomFormAttributePrefix,
  AuthLevel,
  TriggeringFormId,
} = SystemVariableId;

export const operatorItems = {
  isEqual: { id: EQUALS, name: 'is equal' },
  isNotEquals: { id: NOT_EQUALS, name: 'is not equal' },
  isEmpty: { id: IS_EMPTY, name: 'is empty' },
  isNotEmpty: { id: IS_NOT_EMPTY, name: 'is not empty' },
  contains: { id: CONTAINS, name: 'contains' },
  doesNotContain: { id: NOT_CONTAINS, name: 'does not contain' },
  startsWith: { id: STARTS_WITH, name: 'starts with' },
  endsWith: { id: ENDS_WITH, name: 'ends with' },
  greaterThan: { id: GREATER_THAN, name: 'greater than' },
  lessThan: { id: LESS_THAN, name: 'less than' },
  greaterThanOrEqual: {
    id: GREATER_THAN_OR_EQUAL,
    name: 'greater than or equal',
  },
  lessThanOrEqual: { id: LESS_THAN_OR_EQUAL, name: 'less than or equal' },
};

/** Get id from system variable string. */
function getIdFromVariableString(
  variable: FiltersFormRule['variable'],
): string | undefined {
  const [type, ...other] = variable?.split(':') || [];
  // Some of the variables contain : as a part of the id.
  // For example - ContactCustomAttributePrefix
  const id = other.length > 1 ? other.join(':') : other.at(0);
  return type === SystemVariableType.System ? id : undefined;
}

/**
 * Gets selectable variables and returns in shape, how the Select component can
 * consume them.
 */
export function getVariableItems({
  flowType,
  scopeVariables,
  selectedVariable,
  omitSystemVariables,
}: {
  flowType: FlowType;
  scopeVariables: ScopeVariables;
  selectedVariable?: string;
  omitSystemVariables?: SystemVariableId[];
}): FlowCategorySelectItem[] {
  const flowVariableList: FlowCategorySelectItem[] = [];

  if (scopeVariables.available.length) {
    flowVariableList.push(
      ...scopeVariables.available.map((scopeVariable: string) => ({
        id: `${SystemVariableType.Scope}:${scopeVariable}` as FlowCategorySelectItem['id'],
        name: scopeVariable,
        description: 'Flow variables',
      })),
    );
  }

  const description = 'Conversation data';
  const conversationDataList: Omit<FlowCategorySelectItem, 'description'>[] =
    [];
  const contactDataList: Omit<FlowCategorySelectItem, 'description'>[] = [];

  // Pushes variables into the flowCategories list, based on the flowType.
  //
  // We depreciated a few variables what will be pushed into the list,
  // only if they were already selected.
  switch (flowType) {
    case FlowType.FLOW_TYPE_CALL: {
      conversationDataList.push(
        {
          id: getSystemVariableString(CallerNumber),
          name: 'Caller number',
        },
        { id: getSystemVariableString(Date), name: 'Date' },
      );

      contactDataList.push({
        id: getSystemVariableString(ContactCustomAttributePrefix),
        name: 'Contact attribute',
      });

      break;
    }

    case FlowType.FLOW_TYPE_EMAIL: {
      conversationDataList.push(
        { id: getSystemVariableString(SubjectOrBody), name: 'Subject or body' },
        { id: getSystemVariableString(Subject), name: 'Subject' },
        { id: getSystemVariableString(Body), name: 'Body' },
        { id: getSystemVariableString(FromEmail), name: 'From email' },
        { id: getSystemVariableString(FromName), name: 'From name' },
        { id: getSystemVariableString(Channel), name: 'Channel' },
        { id: getSystemVariableString(Direction), name: 'Direction' },
        {
          id: getSystemVariableString(CustomFormAttributePrefix),
          name: 'Form field value',
        },
        {
          id: getSystemVariableString(TriggeringFormId),
          name: 'Triggering form',
        },
      );
      conversationDataList.push({
        id: getSystemVariableString(Language),
        name: 'Language',
      });
      conversationDataList.push(
        { id: getSystemVariableString(ToEmail), name: 'To email' },
        { id: getSystemVariableString(CCEmails), name: 'CC emails' },
        { id: getSystemVariableString(Date), name: 'Date' },
      );

      contactDataList.push({
        id: getSystemVariableString(ContactCustomAttributePrefix),
        name: 'Contact attribute',
      });

      break;
    }

    case FlowType.FLOW_TYPE_MESSENGER: {
      conversationDataList.push(
        { id: getSystemVariableString(FromEmail), name: 'From email' },
        { id: getSystemVariableString(FromName), name: 'From name' },
        { id: getSystemVariableString(AuthLevel), name: 'Auth Level' },
        { id: getSystemVariableString(Language), name: 'Language' },
        {
          id: getSystemVariableString(CustomFormAttributePrefix),
          name: 'Form field value',
        },
      );

      contactDataList.push({
        id: getSystemVariableString(ContactCustomAttributePrefix),
        name: 'Contact attribute',
      });

      break;
    }
  }

  // If there is a deprecated variable selected previously, it will be
  // pushed to the end of the list.
  const deprecatedVariable = getDeprecatedVariable(selectedVariable);

  if (deprecatedVariable) {
    conversationDataList.push(deprecatedVariable);
  }

  const filteredConversationDataList: FlowCategorySelectItem[] =
    conversationDataList
      .map((variable) => ({ ...variable, description }))
      .filter(
        (variable) =>
          !(omitSystemVariables ?? []).find(
            (omit) =>
              variable?.id && variable.id === getSystemVariableString(omit),
          ),
      );

  return [
    ...flowVariableList,
    ...filteredConversationDataList,
    ...contactDataList.map((variable) => ({
      ...variable,
      description: 'Contact data',
    })),
  ];
}

/** Gets deprecated variable item. */
function getDeprecatedVariable(
  variable?: string,
): Omit<FlowCategorySelectItem, 'description'> | null {
  const CSIDString = getSystemVariableString(CSID);
  const receivingNumberString = getSystemVariableString(ReceivingNumber);
  const directionString = getSystemVariableString(Direction);
  const channelString = getSystemVariableString(Channel);

  switch (variable) {
    case CSIDString:
      return { id: CSIDString, name: 'Conversation ID' };
    case receivingNumberString:
      return { id: receivingNumberString, name: 'Receiving number' };
    case directionString:
      return { id: directionString, name: 'Direction' };
    case channelString:
      return { id: channelString, name: 'Channel' };
    default:
      return null;
  }
}

/**
 * Gets valid operator based on the selected variable. A few of the operators
 * are depreciated, so they're only included in the list if they were previously
 * selected for that criteria.
 */
export function getValidOperator(
  variable: FiltersFormRule['variable'],
  options: Partial<{
    matchingRule: FiltersFormRule['matching_rule'];
    variableType: 'set';
  }> = {},
): Operator[] {
  const { matchingRule, variableType } = options;
  const isSystemVariable = variable === SystemVariableType.System;
  const variableId = getIdFromVariableString(variable);
  const isDateVariable = variableId === Date;

  const {
    contains,
    doesNotContain,
    endsWith,
    isEmpty,
    isEqual,
    isNotEquals,
    startsWith,
  } = operatorItems;

  const ruleList: Operator[] = [];

  const currentMatchingRuleItem = Object.values(operatorItems).find(
    ({ id }) => id === matchingRule,
  );

  switch (variableId) {
    case Language:
      ruleList.push(isEqual, isNotEquals);
      break;
    case FromEmail:
    case ToEmail:
    case CCEmails:
      ruleList.push(
        contains,
        doesNotContain,
        isEqual,
        isNotEquals,
        startsWith,
        endsWith,
      );
      break;
    case ContactCustomAttributePrefix:
      // startWith and endsWith are not supported for `Set` type
      const additionalRules =
        variableType !== 'set' ? [startsWith, endsWith] : [];
      ruleList.push(
        isEqual,
        isNotEquals,
        contains,
        doesNotContain,
        ...additionalRules,
      );
      break;
    case CustomFormAttributePrefix:
      ruleList.push(
        isEqual,
        isNotEquals,
        contains,
        doesNotContain,
        startsWith,
        endsWith,
      );
      break;
    case FromName:
    case SubjectOrBody:
      ruleList.push(contains, doesNotContain, isEqual, isNotEquals);
      break;
    case Subject:
      ruleList.push(
        contains,
        doesNotContain,
        isEqual,
        isNotEquals,
        startsWith,
        endsWith,
        isEmpty,
      );
      break;
    case Body:
      ruleList.push(contains, doesNotContain);
      break;
    case CallerNumber:
      ruleList.push(contains, isEqual, isNotEquals);
      break;
    case AuthLevel:
      ruleList.push(isEqual, isNotEquals);
      break;
    case TriggeringFormId:
      ruleList.push(isEqual, isNotEquals);
      break;
    default:
      (Object.keys(operatorItems) as (keyof typeof operatorItems)[]).reduce(
        (acc: Operator[], key: OperatorKey) => {
          if (key === 'isEqual' || key === 'isNotEquals') {
            ruleList.push(operatorItems[key]);
          }
          if (!isSystemVariable && !isDateVariable) {
            ruleList.push(operatorItems[key]);
          }
          return acc;
        },
        [],
      );
  }

  return matchingRule
    ? (uniqWith(
        [...ruleList, currentMatchingRuleItem],
        lodashIsEqual,
      ) as Operator[])
    : ruleList;
}

/** Gets input field placeholder based on the selected variable and operator. */
export function getInputPlaceholder(
  variable: FiltersFormRule['variable'],
  matchingRule: FiltersFormRule['matching_rule'],
): string {
  const defaultPlaceholder = 'Please add a value';

  if (!variable) {
    return defaultPlaceholder;
  }

  const variableId = getIdFromVariableString(variable);

  switch (matchingRule) {
    case CONTAINS:
      switch (variableId) {
        case FromEmail:
        case ToEmail:
        case CCEmails:
        case FromName:
          return 'e.g. friends';
        case SubjectOrBody:
        case Subject:
        case Body:
          return 'e.g. your order';
        case CallerNumber:
          return 'e.g. 123';
        default:
          return defaultPlaceholder;
      }
    case NOT_CONTAINS:
      switch (variableId) {
        case FromEmail:
        case ToEmail:
        case CCEmails:
        case FromName:
          return 'e.g. foes';
        case SubjectOrBody:
        case Subject:
        case Body:
          return 'e.g. your order';
        default:
          return defaultPlaceholder;
      }
    case EQUALS:
      switch (variableId) {
        case FromEmail:
        case ToEmail:
        case CCEmails:
          return 'e.g. friends@dixa.com';
        case FromName:
          return 'e.g. dixa friends';
        case SubjectOrBody:
        case Subject:
          return 'e.g. your order has been shipped';
        default:
          return defaultPlaceholder;
      }
    case NOT_EQUALS:
      switch (variableId) {
        case FromEmail:
        case ToEmail:
        case CCEmails:
          return 'e.g. foes@dixa.com';
        case FromName:
          return 'e.g. dixa foes';
        case SubjectOrBody:
        case Subject:
          return 'e.g. your order has been shipped';
        default:
          return defaultPlaceholder;
      }
    case STARTS_WITH:
      switch (variableId) {
        case FromEmail:
        case ToEmail:
        case CCEmails:
          return 'e.g. no-reply';
        case CallerNumber:
          return 'e.g. 0045';
        default:
          return defaultPlaceholder;
      }
    case ENDS_WITH:
      switch (variableId) {
        case FromEmail:
        case ToEmail:
        case CCEmails:
          return 'e.g. @dixa.com';
        default:
          return defaultPlaceholder;
      }
    default:
      return defaultPlaceholder;
  }
}
